import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import useAccount from '@/hooks/useAccount.js';
import AccountWorkspace from '@/components/account/AccountWorkspace.jsx';
import DealWorkspace from '@/components/account/DealWorkspace.jsx';
import { Button } from '@/components/ui/button.jsx';
import { RefreshCw } from 'lucide-react';
import { getDeal } from '@/services/deals.service.js';

// Deep-link healer (Ship 4a item 3): queue/follow-ups/quote-inbox links use
// /account/{dealId}/deal/{dealId} when only the deal id is known, but the
// account resolver 404s a deal id (so those links error-screened). Resolve
// the deal's account_id and hop to the canonical URL once; search params
// (incl. ?door=) survive the hop.
function CanonicalDealRedirect({ idOrSlug, dealId, errorCard }) {
  const location = useLocation();
  const [accountId, setAccountId] = useState(undefined); // undefined = resolving
  useEffect(() => {
    let dead = false;
    getDeal(dealId)
      .then((d) => { if (!dead) setAccountId(d?.deal?.account_id ? String(d.deal.account_id) : null); })
      .catch(() => { if (!dead) setAccountId(null); });
    return () => { dead = true; };
  }, [dealId]);
  if (accountId === undefined) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-text-muted">
        Finding the deal&rsquo;s account&hellip;
      </div>
    );
  }
  if (!accountId || accountId === String(idOrSlug)) return errorCard;
  return (
    <Navigate
      to={{ pathname: `/account/${encodeURIComponent(accountId)}/deal/${encodeURIComponent(dealId)}`, search: location.search }}
      replace
    />
  );
}

export default function Account() {
  const { idOrSlug, dealId } = useParams();
  const { data, error, loading, refresh } = useAccount(idOrSlug);

  if (loading && !data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-text-muted">
        Loading account…
      </div>
    );
  }

  if (error && !data) {
    const errorCard = (
      <div className="mx-auto max-w-md p-4 text-center">
        <p className="rounded-card border border-accent/40 bg-accent-soft px-4 py-3 text-sm text-accent">
          Couldn&apos;t load the account workspace.
          <br />
          <span className="font-mono text-xs">
            {error.message || error.code || 'unknown'}
          </span>
        </p>
        <Button onClick={refresh} variant="outline" className="mt-4">
          <RefreshCw size={16} strokeWidth={1.5} />
          Retry
        </Button>
      </div>
    );
    // Deal deep link whose account segment did not resolve → heal it.
    if (dealId) {
      return <CanonicalDealRedirect idOrSlug={idOrSlug} dealId={dealId} errorCard={errorCard} />;
    }
    return errorCard;
  }

  // Focused deal → the shell-integrated deal workspace (Task I.2). Account
  // overview (no dealId) keeps the existing workspace until I.3 reshapes it.
  return dealId ? (
    <DealWorkspace data={data} dealId={dealId} onChange={refresh} />
  ) : (
    <AccountWorkspace data={data} dealId={dealId} onChange={refresh} />
  );
}
